import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Bars3Icon, 
  XMarkIcon,
  ShoppingBagIcon,
  UserIcon,
  HeartIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Inicio', href: '/' },
    { name: 'Productos', href: '/productos' },
    { name: 'Personalizado', href: '/personalizado' },
    // { name: 'Telas', href: '/telas' }, // Oculto temporalmente hasta tener imágenes
    { name: 'Contacto', href: '/contacto' },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gray-50 gradient-mesh flex flex-col">
      {/* Header - Glassmorphism */}
      <header className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-white/70 backdrop-blur-xl shadow-lg shadow-pink-500/5 border-b border-white/50' 
          : 'bg-white/50 backdrop-blur-md'
      }`}>
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 md:w-11 md:h-11 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-pink-500/30 group-hover:shadow-pink-500/50 transition-all duration-300 group-hover:scale-105">
                  <span className="text-white font-bold text-lg md:text-xl">G</span>
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-bold font-display bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                    GE-NO MODA
                  </h1>
                  <p className="text-[10px] text-pink-500 font-medium tracking-widest uppercase hidden sm:block">
                    Modista Profesional
                  </p>
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 ${
                    isActive(item.href)
                      ? 'text-pink-600 bg-pink-50/80'
                      : 'text-gray-600 hover:text-pink-600 hover:bg-pink-50/50'
                  }`}
                >
                  {item.name}
                  {isActive(item.href) && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full" />
                  )}
                </Link>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center space-x-1">
              <button className="p-2.5 text-gray-400 hover:text-pink-500 hover:bg-pink-50/50 rounded-xl transition-all duration-300">
                <MagnifyingGlassIcon className="h-5 w-5" />
              </button>
              <button className="p-2.5 text-gray-400 hover:text-pink-500 hover:bg-pink-50/50 rounded-xl transition-all duration-300 relative">
                <HeartIcon className="h-5 w-5" />
              </button>
              <button className="p-2.5 text-gray-400 hover:text-pink-500 hover:bg-pink-50/50 rounded-xl transition-all duration-300 relative">
                <ShoppingBagIcon className="h-5 w-5" />
              </button>
              <Link
                to="/perfil"
                className="p-2.5 text-gray-400 hover:text-pink-500 hover:bg-pink-50/50 rounded-xl transition-all duration-300"
              >
                <UserIcon className="h-5 w-5" />
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center space-x-1">
              <button className="p-2 text-gray-400 hover:text-pink-500 rounded-xl transition-colors">
                <ShoppingBagIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-500 hover:text-pink-500 rounded-xl transition-all duration-300 hover:bg-pink-50/50"
              >
                {mobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation - Glassmorphism */}
        <div className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
          mobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="bg-white/80 backdrop-blur-xl border-t border-pink-100/50">
            <div className="container-custom py-4">
              <nav className="space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-3 text-base font-medium rounded-xl transition-all duration-300 ${
                      isActive(item.href)
                        ? 'bg-gradient-to-r from-pink-50 to-purple-50 text-pink-600 shadow-sm'
                        : 'text-gray-600 hover:bg-pink-50/50 hover:text-pink-600'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
              
              <div className="mt-4 pt-4 border-t border-pink-100/30 space-y-1">
                <Link
                  to="/perfil"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 text-base font-medium text-gray-600 hover:bg-pink-50/50 hover:text-pink-600 rounded-xl transition-all duration-300"
                >
                  Mi Perfil
                </Link>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 text-base font-medium text-pink-600 hover:bg-pink-50 rounded-xl transition-all duration-300"
                >
                  Iniciar Sesión
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">{children}</main>

      {/* Footer - Modern Dark with blur accents */}
      <footer className="relative bg-gray-950 text-white overflow-hidden">
        {/* Decorative blur orbs */}
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-pink-500/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-purple-500/10 rounded-full blur-[80px] pointer-events-none" />
        
        <div className="container-custom py-16 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Company Info */}
            <div className="space-y-5">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-pink-500/20">
                  <span className="text-white font-bold text-xl">G</span>
                </div>
                <span className="text-xl font-bold font-display">GE-NO MODA</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Modista profesional especializada en confección a medida, reparaciones 
                y productos textiles de alta calidad para el hogar.
              </p>
              <div className="flex space-x-3">
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-pink-500/20 border border-white/10 hover:border-pink-400/30 rounded-xl flex items-center justify-center text-gray-400 hover:text-pink-400 transition-all duration-300">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
                  </svg>
                </a>
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-pink-500/20 border border-white/10 hover:border-pink-400/30 rounded-xl flex items-center justify-center text-gray-400 hover:text-pink-400 transition-all duration-300">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-green-500/20 border border-white/10 hover:border-green-400/30 rounded-xl flex items-center justify-center text-gray-400 hover:text-green-400 transition-all duration-300">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.886 3.488"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-pink-400 mb-5">Enlaces</h3>
              <ul className="space-y-3">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-gray-400 hover:text-white transition-colors duration-300 text-sm flex items-center group"
                    >
                      <span className="w-0 group-hover:w-4 h-px bg-pink-500 mr-0 group-hover:mr-2 transition-all duration-300" />
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-pink-400 mb-5">Servicios</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="hover:text-white transition-colors duration-300 cursor-default">Vestidos a la Medida</li>
                <li className="hover:text-white transition-colors duration-300 cursor-default">Conjuntos de Cocina</li>
                <li className="hover:text-white transition-colors duration-300 cursor-default">Reparaciones</li>
                <li className="hover:text-white transition-colors duration-300 cursor-default">Ropa de Cama</li>
                <li className="hover:text-white transition-colors duration-300 cursor-default">Cortinas</li>
                <li className="hover:text-white transition-colors duration-300 cursor-default">Trajes de Baile</li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-pink-400 mb-5">Contacto</h3>
              <div className="space-y-4 text-sm text-gray-400">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-sm">📱</span>
                  </div>
                  <span>+52 55 1234 5678</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-sm">✉️</span>
                  </div>
                  <span>contacto@ge-no-moda.com</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm">📍</span>
                  </div>
                  <div>
                    <p className="text-white/80 font-medium mb-1">Zona de Cobertura</p>
                    <p>Chalco, Ixtapaluca, Amecameca</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/5 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-500">
              © 2026 GE-NO MODA. Todos los derechos reservados.
            </p>
            <p className="text-xs text-gray-600 mt-2 md:mt-0">
              Hecho con 💕 en México
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;